import { useAccount, useReadContract } from 'wagmi'
import { useMemo } from 'react'
import { usePresales } from './usePresales'
import { getContractAddresses, ERC20_ABI, parseUSDC } from '@/lib/contracts'

interface ClaimablePosition {
  presaleId: string
  question: string
  outcome: 'YES' | 'NO'
  tokenAddress: string
  balance: bigint
  balanceFormatted: number
  isWinning: boolean
  answer: number // 0 = NO won, 1 = YES won
}

export function useClaimableWinnings() {
  const { address } = useAccount()
  const { data: presales, isLoading: isLoadingPresales } = usePresales()
  const contracts = getContractAddresses()

  // Filter only resolved presales
  const resolvedPresales = useMemo(() => {
    if (!presales) return []
    return presales.filter(p => p.state === 'Resolved' && p.resolved)
  }, [presales])

  // For each resolved presale, get user's YES and NO token balances
  const positions: ClaimablePosition[] = []

  // Read YES token balances for all resolved presales
  const yesBalances = resolvedPresales.map(presale => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { data } = useReadContract({
      address: presale.yesToken as `0x${string}`,
      abi: ERC20_ABI,
      functionName: 'balanceOf',
      args: address ? [address] : undefined,
      query: {
        enabled: !!address && !!presale.yesToken && presale.yesToken !== '0x0000000000000000000000000000000000000000'
      }
    })
    return {
      presaleId: presale.id,
      question: presale.question,
      balance: data as bigint || 0n,
      tokenAddress: presale.yesToken || '',
      outcome: 'YES' as const,
      answer: presale.forceResolvedAnswer || 0
    }
  })

  // Read NO token balances for all resolved presales
  const noBalances = resolvedPresales.map(presale => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { data } = useReadContract({
      address: presale.noToken as `0x${string}`,
      abi: ERC20_ABI,
      functionName: 'balanceOf',
      args: address ? [address] : undefined,
      query: {
        enabled: !!address && !!presale.noToken && presale.noToken !== '0x0000000000000000000000000000000000000000'
      }
    })
    return {
      presaleId: presale.id,
      question: presale.question,
      balance: data as bigint || 0n,
      tokenAddress: presale.noToken || '',
      outcome: 'NO' as const,
      answer: presale.forceResolvedAnswer || 0
    }
  })

  // Combine and filter positions with non-zero balances
  const claimablePositions = useMemo(() => {
    const allPositions = [...yesBalances, ...noBalances]
    
    return allPositions
      .filter(pos => pos.balance > 0n)
      .map(pos => ({
        ...pos,
        balanceFormatted: parseUSDC(pos.balance),
        isWinning: (pos.outcome === 'YES' && pos.answer === 1) || (pos.outcome === 'NO' && pos.answer === 0)
      }))
      .filter(pos => pos.isWinning) // Only show winning positions
  }, [yesBalances, noBalances])

  // Calculate total claimable value
  const totalClaimable = useMemo(() => {
    return claimablePositions.reduce((sum, pos) => sum + pos.balanceFormatted, 0)
  }, [claimablePositions])

  return {
    claimablePositions,
    totalClaimable,
    isLoading: isLoadingPresales,
    hasClaimable: claimablePositions.length > 0
  }
}

